import {
  MaterialReactTable,
  MRT_ShowHideColumnsButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleFullScreenButton,
  useMaterialReactTable,
} from "material-react-table";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { List, Refresh } from "@mui/icons-material";
import ClearIcon from "@mui/icons-material/FilterAltOff";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ClearSortingIcon from "@mui/icons-material/MobiledataOff";
import { Box, IconButton, Link, Tooltip, Typography } from "@mui/material";
import { useMediaQuery } from "@mui/material";
import { keyframes, lighten } from "@mui/material/styles";
import { useTheme } from "@mui/material/styles";

import Widget from "../ui/widget.js";
import WidgetSurveyContent from "../ui/widgetSurveyContent.js";
import WidgetTitle from "../ui/widgetTitle.js";

const MAX_PAGES_TO_DISPLAY = 200;

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const BotpressSiteMap = props => {
  const { widgetSize = "large", minHeight = "333px" } = props;

  const theme = useTheme();
  const mobileLayout = useMediaQuery(theme.breakpoints.down("sm"));
  const tableContainerRef = useRef(null);

  const organizationODM = useSelector(state => state.userSession.organizationODM);

  const publicWebsite =
    organizationODM?.omniData?.publicWebsite || organizationODM?.publicWebsite || "";

  const [siteMapRows, setSiteMapRows] = useState([]);
  const [pageCount, setPageCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshSpinning, setIsRefreshSpinning] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [sorting, setSorting] = useState([
    {
      id: "title",
      desc: false,
    },
  ]);

  const [columnFilters, setColumnFilters] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [globalFilter, setGlobalFilter] = useState("");
  const [rowSelection, setRowSelection] = useState({});

  const [columnOrder, setColumnOrder] = useState([
    "mrt-row-expand",
    "actions",
    "mrt-row-select",
    "lastModified",
    "status",
    "title",
    "displayUrl",
  ]);

  const getProxyUrl = useCallback(targetUrl => {
    return `/sitemap-proxy?target=${encodeURIComponent(targetUrl)}`;
  }, []);

  const getSiteMapUrls = useCallback(websiteUrl => {
    const url = new URL(websiteUrl);

    if (url.pathname.endsWith(".xml")) {
      return [url.href];
    }

    const origins = [url.origin];

    if (url.hostname.startsWith("www.")) {
      origins.push(`${url.protocol}//${url.hostname.replace("www.", "")}`);
    } else {
      origins.push(`${url.protocol}//www.${url.hostname}`);
    }

    const siteMapUrls = origins.flatMap(origin => [
      `${origin}/sitemap.xml`,
      `${origin}/sitemap_index.xml`,
    ]);

    return Array.from(new Set(siteMapUrls));
  }, []);

  const getPageTitle = useCallback(pageUrl => {
    try {
      const url = new URL(pageUrl);
      const pathParts = url.pathname
        .split("/")
        .filter(pathPart => pathPart)
        .map(pathPart => pathPart.replace(/-/g, " ").replace(/\b\w/g, char => char.toUpperCase()));

      if (pathParts.length === 0) {
        return "Home";
      }

      return pathParts[pathParts.length - 1];
    } catch (error) {
      return pageUrl;
    }
  }, []);

  const getSiteMapTitle = useCallback(siteMapUrl => {
    try {
      const url = new URL(siteMapUrl);
      const pathParts = url.pathname.split("/").filter(pathPart => pathPart);

      return pathParts[pathParts.length - 1] || siteMapUrl;
    } catch (error) {
      return siteMapUrl;
    }
  }, []);

  const getDisplayUrl = useCallback(pageUrl => {
    try {
      const url = new URL(pageUrl);

      return url.href;
    } catch (error) {
      return pageUrl;
    }
  }, []);

  const formatDateParts = useCallback(dateValue => {
    if (!dateValue) {
      return {
        dateText: "",
        timeText: "",
      };
    }

    const date = new Date(dateValue);

    if (Number.isNaN(date.getTime())) {
      return {
        dateText: dateValue,
        timeText: "",
      };
    }

    return {
      dateText: date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      }),
      timeText: date.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "numeric",
        hour12: true,
      }),
    };
  }, []);

  const getStatusSx = useCallback(
    status => {
      switch (status) {
        case "Sitemap":
          return {
            backgroundColor: lighten(theme.palette.commonground.contrastText, 0.4),
            color: theme.palette.commonground.contrastText,
          };
        case "Up To Date":
          return {
            backgroundColor: theme.palette.success.main,
            color: theme.palette.success.contrastText,
          };
        case "Needs Update":
          return {
            backgroundColor: theme.palette.warning.main,
            color: theme.palette.warning.contrastText,
          };
        case "New":
        default:
          return {
            backgroundColor: theme.palette.info.main,
            color: theme.palette.info.contrastText,
          };
      }
    },
    [theme],
  );

  const parseSiteMapXml = useCallback(
    xmlText => {
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xmlText, "application/xml");

      const parserError = xmlDoc.querySelector("parsererror");

      if (parserError) {
        throw new Error("Unable to parse sitemap.xml");
      }

      const nestedSiteMaps = Array.from(xmlDoc.getElementsByTagName("sitemap"))
        .map((siteMapNode, index) => {
          const loc = siteMapNode.getElementsByTagName("loc")[0]?.textContent || "";
          const lastModified = siteMapNode.getElementsByTagName("lastmod")[0]?.textContent || "";

          return {
            id: loc || `sitemap-${index}`,
            lastModified,
            loc,
          };
        })
        .filter(siteMap => siteMap.loc);

      const pageUrls = Array.from(xmlDoc.getElementsByTagName("url")).map((urlNode, index) => {
        const loc = urlNode.getElementsByTagName("loc")[0]?.textContent || "";
        const lastModified = urlNode.getElementsByTagName("lastmod")[0]?.textContent || "";

        return {
          id: loc || `page-${index}`,
          actions: "Open",
          lastModified,
          status: "New",
          title: getPageTitle(loc),
          url: loc,
          displayUrl: getDisplayUrl(loc),
        };
      });

      return {
        nestedSiteMaps,
        pageUrls: pageUrls.filter(page => page.url),
      };
    },
    [getDisplayUrl, getPageTitle],
  );

  const loadNestedSiteMaps = useCallback(
    async nestedSiteMaps => {
      const limitedNestedSiteMaps = nestedSiteMaps.slice(0, 10);

      const nestedRows = await Promise.all(
        limitedNestedSiteMaps.map(async siteMap => {
          try {
            const response = await fetch(getProxyUrl(siteMap.loc));

            if (!response.ok) {
              return {
                id: siteMap.loc,
                actions: "Open",
                lastModified: siteMap.lastModified,
                status: "Sitemap",
                title: getSiteMapTitle(siteMap.loc),
                url: siteMap.loc,
                displayUrl: getDisplayUrl(siteMap.loc),
                subRows: [],
              };
            }

            const xmlText = await response.text();
            const parsedSiteMap = parseSiteMapXml(xmlText);

            return {
              id: siteMap.loc,
              actions: "Open",
              lastModified: siteMap.lastModified,
              status: "Sitemap",
              title: getSiteMapTitle(siteMap.loc),
              url: siteMap.loc,
              displayUrl: getDisplayUrl(siteMap.loc),
              subRows: parsedSiteMap.pageUrls.slice(0, MAX_PAGES_TO_DISPLAY),
            };
          } catch (error) {
            console.error("Unable to load nested sitemap", siteMap.loc, error);

            return {
              id: siteMap.loc,
              actions: "Open",
              lastModified: siteMap.lastModified,
              status: "Sitemap",
              title: getSiteMapTitle(siteMap.loc),
              url: siteMap.loc,
              displayUrl: getDisplayUrl(siteMap.loc),
              subRows: [],
            };
          }
        }),
      );

      return nestedRows;
    },
    [getDisplayUrl, getProxyUrl, getSiteMapTitle, parseSiteMapXml],
  );

  const flattenSiteMapRows = useCallback(rows => {
    return rows.flatMap(row => {
      if (row.subRows?.length > 0) {
        return row.subRows;
      }

      return row.status === "Sitemap" ? [] : [row];
    });
  }, []);

  const getTotalPageCount = useCallback(rows => {
    return rows.reduce((total, row) => {
      if (row.subRows?.length > 0) {
        return total + row.subRows.length;
      }

      return row.status === "Sitemap" ? total : total + 1;
    }, 0);
  }, []);

  const loadSiteMap = useCallback(async () => {
    setErrorMessage("");
    setSiteMapRows([]);
    setPageCount(0);
    setExpanded({});
    setIsLoading(true);
    setIsRefreshSpinning(true);

    if (!publicWebsite) {
      setIsLoading(false);
      setIsRefreshSpinning(false);
      return;
    }

    try {
      const siteMapUrls = getSiteMapUrls(publicWebsite);

      let parsedSiteMap = null;

      for (const siteMapUrl of siteMapUrls) {
        try {
          const response = await fetch(getProxyUrl(siteMapUrl));

          if (!response.ok) {
            continue;
          }

          const xmlText = await response.text();
          const parsedCandidate = parseSiteMapXml(xmlText);

          if (parsedCandidate.pageUrls.length > 0 || parsedCandidate.nestedSiteMaps.length > 0) {
            parsedSiteMap = parsedCandidate;
            break;
          }
        } catch (error) {
          console.error("Unable to load sitemap candidate", siteMapUrl, error);
        }
      }

      if (parsedSiteMap == null) {
        throw new Error("Unable to load sitemap.");
      }

      let rows = parsedSiteMap.pageUrls.slice(0, MAX_PAGES_TO_DISPLAY);

      if (parsedSiteMap.nestedSiteMaps.length > 0) {
        rows = await loadNestedSiteMaps(parsedSiteMap.nestedSiteMaps);
      }

      setSiteMapRows(rows);
      setPageCount(getTotalPageCount(rows));

      if (getTotalPageCount(rows) === 0) {
        setErrorMessage("No pages were found in this sitemap.");
      }
    } catch (error) {
      setErrorMessage(
        "Unable to load the sitemap for this website. The site may not have a sitemap.xml file, or it may block browser requests.",
      );
    } finally {
      setIsLoading(false);

      setTimeout(() => {
        setIsRefreshSpinning(false);
      }, 800);
    }
  }, [
    getProxyUrl,
    getSiteMapUrls,
    getTotalPageCount,
    loadNestedSiteMaps,
    parseSiteMapXml,
    publicWebsite,
  ]);

  useEffect(() => {
    loadSiteMap();
  }, [loadSiteMap]);

  const columns = useMemo(
    () => [
      {
        accessorKey: "actions",
        header: "Actions",
        size: 80,
        minSize: 80,
        maxSize: 80,
        grow: false,
        enableColumnFilter: false,
        enableColumnFilterModes: false,
        enableSorting: false,
        muiTableHeadCellProps: {
          align: "center",
        },
        muiTableBodyCellProps: {
          align: "center",
          sx: {
            padding: "4px 6px",
            borderLeft: `1px solid ${lighten(theme.palette.commonground.contrastText, 0.5)}`,
          },
        },
        Cell: ({ row }) => (
          <Tooltip title="Open page" arrow>
            <IconButton
              onClick={() => {
                window.open(row.original.url, "_blank");
              }}
              sx={{
                color: theme.palette.secondary.main,
                borderRadius: 16,
                width: 34,
                height: 34,
                padding: "8px",
                border: `2px solid ${theme.palette.secondary.main}`,
              }}
            >
              <List />
            </IconButton>
          </Tooltip>
        ),
      },
      {
        accessorKey: "lastModified",
        header: "Date",
        size: 75,
        maxSize: 90,
        grow: false,
        enableColumnFilterModes: false,
        enableSorting: true,
        enableColumnFilter: false,
        muiTableHeadCellProps: {
          align: "center",
        },
        muiTableBodyCellProps: {
          align: "center",
          sx: {
            fontSize: "32px",
            borderLeft: `1px solid ${lighten(theme.palette.commonground.contrastText, 0.5)}`,
            padding: "4px 3px",
          },
        },
        Cell: ({ cell }) => {
          const { dateText, timeText } = formatDateParts(cell.getValue());

          return (
            <Box component="span">
              <Typography
                variant="h6"
                sx={{
                  textAlign: "center",
                  marginBottom: "2px",
                  borderBottom: dateText ? `2px solid` : "none",
                  fontSize: "0.9rem",
                }}
              >
                {dateText}
              </Typography>

              <Typography
                variant="body1"
                fontWeight={100}
                sx={{
                  textAlign: "center",
                  fontSize: "0.75rem",
                }}
              >
                {timeText}
              </Typography>
            </Box>
          );
        },
      },
      {
        accessorKey: "status",
        header: "Status",
        size: 160,
        minSize: 160,
        grow: false,
        enableColumnFilterModes: false,
        enableSorting: true,
        filterFn: "equals",
        filterSelectOptions: [
          { label: "Sitemap", value: "Sitemap" },
          { label: "New", value: "New" },
          { label: "Up To Date", value: "Up To Date" },
          { label: "Needs Update", value: "Needs Update" },
        ],
        filterVariant: "select",
        muiTableHeadCellProps: {
          align: "center",
        },
        muiTableBodyCellProps: {
          align: "center",
          sx: {
            borderLeft: `1px solid ${lighten(theme.palette.commonground.contrastText, 0.5)}`,
            padding: "4px 3px",
          },
        },
        Cell: ({ cell }) => (
          <Box
            component="span"
            sx={{
              ...getStatusSx(cell.getValue()),
              borderRadius: "0.25rem",
              display: "inline-block",
              minWidth: "110px",
              padding: theme.spacing(1),
            }}
          >
            <Typography variant="button" sx={{ fontWeight: "bold" }}>
              {cell.getValue()}
            </Typography>
          </Box>
        ),
      },
      {
        accessorKey: "title",
        header: "Title",
        size: 300,
        minSize: 300,
        grow: false,
        enableColumnFilterModes: false,
        enableSorting: true,
        muiTableHeadCellProps: {
          align: "left",
        },
        muiTableBodyCellProps: {
          sx: {
            whiteSpace: "normal",
            wordWrap: "break-word",
            borderLeft: `1px solid ${lighten(theme.palette.commonground.contrastText, 0.5)}`,
            padding: "4px 3px",
          },
        },
        Cell: ({ cell, row }) => (
          <Typography
            variant="subtitle1"
            sx={{
              textAlign: "left",
              fontSize: row.original.status === "Sitemap" ? "0.82rem" : "0.78rem",
              fontWeight: row.original.status === "Sitemap" ? "bold" : "normal",
              lineHeight: 1.1,
              whiteSpace: "normal",
              wordBreak: "break-word",
            }}
          >
            {cell.getValue()}
          </Typography>
        ),
      },
      {
        accessorKey: "displayUrl",
        header: "URL",
        size: 900,
        minSize: 450,
        grow: true,
        enableColumnFilterModes: false,
        enableSorting: true,
        muiTableHeadCellProps: {
          align: "left",
        },
        muiTableBodyCellProps: {
          sx: {
            whiteSpace: "normal",
            wordWrap: "break-word",
            borderLeft: `1px solid ${lighten(theme.palette.commonground.contrastText, 0.5)}`,
            padding: "4px 3px",
          },
        },
        Cell: ({ row }) => (
          <Link
            href={row.original.url}
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
            sx={{
              whiteSpace: "normal",
              wordBreak: "break-word",
              fontSize: "0.76rem",
              lineHeight: 1.1,
              fontWeight: row.original.status === "Sitemap" ? "bold" : "normal",
            }}
          >
            {row.original.displayUrl}
          </Link>
        ),
      },
    ],
    [formatDateParts, getStatusSx, theme],
  );

  const table = useMaterialReactTable({
    columns,
    data: globalFilter ? flattenSiteMapRows(siteMapRows) : siteMapRows,
    enableColumnFilterModes: false,
    enableColumnOrdering: false,
    enableColumnResizing: false,
    enableExpanding: !globalFilter,
    enableExpandAll: false,
    enableGlobalFilter: true,
    filterFromLeafRows: true,
    maxLeafRowFilterDepth: 1,
    enableGrouping: false,
    enableColumnPinning: false,
    enableFacetedValues: true,
    enableRowActions: false,
    enableRowSelection: true,
    enableStickyHeader: true,
    enableColumnActions: false,
    enableDensityToggle: false,
    enableFullScreenToggle: true,
    enableHiding: true,
    enablePagination: true,
    enableTopToolbar: true,
    enableBottomToolbar: true,
    paginateExpandedRows: false,
    positionGlobalFilter: "left",
    positionToolbarAlertBanner: "bottom",
    layoutMode: "grid",
    initialState: {
      density: "compact",
      showGlobalFilter: true,
      columnVisibility: {
        actions: false,
      },
      pagination: {
        pageIndex: 0,
        pageSize: 20,
      },
    },
    defaultColumn: {
      minSize: 60,
      maxSize: 220,
      size: 100,
      grow: false,
    },
    state: {
      columnFilters,
      columnOrder,
      expanded,
      globalFilter,
      isLoading,
      rowSelection,
      sorting,
    },
    getRowId: row => row.id,
    getSubRows: row => (globalFilter ? undefined : row.subRows),
    onExpandedChange: setExpanded,
    onGlobalFilterChange: setGlobalFilter,
    onRowSelectionChange: setRowSelection,
    onColumnOrderChange: setColumnOrder,
    onColumnFiltersChange: setColumnFilters,
    onSortingChange: setSorting,
    paginationDisplayMode: "pages",
    enableFirstPageButton: false,
    enableLastPageButton: false,
    muiSearchTextFieldProps: {
      placeholder: "Search site map",
      size: "small",
      sx: {
        minWidth: "300px",
        marginLeft: theme.spacing(1),
        "& .MuiInputAdornment-root .MuiIconButton-root": {
          border: "none !important",
          backgroundColor: "transparent !important",
          color: `${theme.palette.secondary.main} !important`,
          marginLeft: "0px !important",
          padding: "4px !important",
          borderRadius: "50%",
        },
        "& .MuiInputAdornment-root .MuiIconButton-root:hover": {
          border: "none !important",
          backgroundColor: "rgba(0, 0, 0, 0.2) !important",
        },
        "& .MuiInputAdornment-root .MuiIconButton-root:active": {
          border: "none !important",
          backgroundColor: "rgba(0, 0, 0, 0.3) !important",
        },
      },
    },
    muiPaginationProps: {
      color: "primary",
      shape: "rounded",
      showRowsPerPage: true,
      variant: "outlined",
    },
    muiToolbarAlertBannerProps: {
      sx: {
        backgroundColor: "transparent !important",
        boxShadow: "none",
        color: theme.palette.commonground.contrastText,

        "&.MuiAlert-root": {
          backgroundColor: "transparent !important",
          boxShadow: "none",
        },

        "& .MuiAlert-message": {
          color: theme.palette.commonground.contrastText,
        },

        "& .MuiAlert-action": {
          color: theme.palette.secondary.main,
        },

        "& button": {
          color: `${theme.palette.secondary.main} !important`,
        },

        "& .MuiButton-root": {
          color: `${theme.palette.secondary.main} !important`,
        },
      },
    },
    muiTopToolbarProps: {
      sx: {
        backgroundColor: theme.palette.commonground.main,
        color: theme.palette.secondary.main,
        "& .MuiIconButton-root": {
          marginLeft: theme.spacing(1),
          backgroundColor: theme.palette.commonground.main,
          color: theme.palette.secondary.main,
          border: `2px solid ${theme.palette.secondary.main}`,
          "&:hover": {
            color: theme.palette.secondary.main,
            backgroundColor: theme.palette.secondary.contrastText,
            border: `2px solid ${theme.palette.secondary.main}`,
          },
        },
        "& .MuiInputAdornment-root .MuiIconButton-root": {
          border: "none !important",
          backgroundColor: "transparent !important",
          marginLeft: "0px !important",
          color: `${theme.palette.secondary.main} !important`,
          padding: "4px !important",
          borderRadius: "50%",
        },
        "& .MuiInputAdornment-root .MuiIconButton-root:hover": {
          border: "none !important",
          backgroundColor: "rgba(0, 0, 0, 0.2) !important",
        },
        "& .MuiInputAdornment-root .MuiIconButton-root:active": {
          border: "none !important",
          backgroundColor: "rgba(0, 0, 0, 0.3) !important",
        },
        "& .MuiIconButton-root-active": {
          marginLeft: theme.spacing(1),
          color: theme.palette.secondary.contrastText,
          backgroundColor: theme.palette.secondary.main,
          border: `2px solid ${theme.palette.secondary.main}`,
          "&:hover": {
            color: theme.palette.secondary.main,
            backgroundColor: theme.palette.secondary.contrastText,
            border: `2px solid ${theme.palette.secondary.main}`,
          },
        },
      },
    },
    muiBottomToolbarProps: {
      sx: {
        backgroundColor: theme.palette.commonground.main,
        color: theme.palette.commonground.contrastText,
        height: "64px",
        minHeight: "64px",
      },
    },
    muiTablePaperProps: {
      sx: {
        boxShadow: "none",
        height: "auto",
        overflow: "visible",
      },
    },
    muiTableContainerProps: {
      ref: tableContainerRef,
      sx: mobileLayout
        ? {
            minHeight: "0px",
            height: "auto",
            maxHeight: "none",
            overflowY: "visible",
            overflowX: "hidden",
            backgroundColor: theme.palette.commonground.main,
            color: theme.palette.commonground.main,
            borderLeft: `1px solid ${lighten(theme.palette.commonground.contrastText, 0.5)}`,
            borderRight: `1px solid ${lighten(theme.palette.commonground.contrastText, 0.5)}`,
          }
        : {
            minHeight: "0px",
            height: "auto",
            maxHeight: "none",
            overflowY: "visible",
            overflowX: "hidden",
            backgroundColor: theme.palette.commonground.main,
            color: theme.palette.commonground.main,
            border: `solid 0.5px ${theme.palette.divider}`,
          },
    },
    muiTableProps: {
      sx: {
        backgroundColor: theme.palette.commonground.main,
        color: theme.palette.commonground.main,
        tableLayout: "fixed",
        width: "100%",
      },
    },
    muiTableHeadCellProps: {
      sx: {
        backgroundColor: lighten(theme.palette.commonground.contrastText, 0.5),
        color: theme.palette.commonground.contrastText,
        justifyContent: "start",
        fontWeight: "bold",
        fontSize: "0.72rem",
        padding: "3px 3px",
        whiteSpace: "nowrap",
      },
    },
    displayColumnDefOptions: {
      "mrt-row-expand": {
        size: 35,
        grow: false,
        Header: ({ table }) => (
          <Tooltip title="Expand selected sitemap" arrow>
            <IconButton
              onClick={() => {
                const selectedParentRows = table
                  .getSelectedRowModel()
                  .flatRows.filter(row => row.original.subRows?.length > 0);

                if (selectedParentRows.length === 0) {
                  return;
                }

                const shouldExpand = selectedParentRows.some(row => !row.getIsExpanded());

                setExpanded(previousExpanded => {
                  const updatedExpanded = { ...previousExpanded };

                  selectedParentRows.forEach(row => {
                    if (shouldExpand) {
                      updatedExpanded[row.id] = true;
                    } else {
                      delete updatedExpanded[row.id];
                    }
                  });

                  return updatedExpanded;
                });
              }}
              sx={{
                color: theme.palette.secondary.main,
                padding: "4px",
              }}
            >
              <KeyboardArrowDownIcon />
            </IconButton>
          </Tooltip>
        ),
        muiTableHeadCellProps: {
          align: "center",
        },
        muiTableBodyCellProps: {
          align: "center",
          sx: {
            color: theme.palette.secondary.main,
            padding: "4px 2px",
          },
        },
      },
      "mrt-row-select": {
        size: 25,
        grow: false,
        enableHiding: false,
        muiTableHeadCellProps: {
          align: "center",
        },
        muiTableBodyCellProps: {
          align: "center",
          sx: {
            color: theme.palette.secondary.main,
            padding: "4px 2px",
          },
        },
      },
    },
    renderToolbarInternalActions: ({ table }) => (
      <Box>
        {sorting.length > 0 && (
          <Tooltip title="Clear All Sorting" arrow>
            <IconButton
              className="MuiIconButton-root-active"
              onClick={() => {
                setSorting([]);
              }}
            >
              <ClearSortingIcon />
            </IconButton>
          </Tooltip>
        )}

        {columnFilters.length > 0 && (
          <Tooltip title="Clear All Filters" arrow>
            <IconButton
              className="MuiIconButton-root-active"
              onClick={() => {
                setColumnFilters([]);
              }}
            >
              <ClearIcon />
            </IconButton>
          </Tooltip>
        )}

        <Tooltip title="Refresh site map" arrow>
          <IconButton onClick={loadSiteMap} disabled={isLoading || !publicWebsite}>
            <Refresh
              sx={{
                animation: isRefreshSpinning ? `${spin} 0.8s linear` : "none",
              }}
            />
          </IconButton>
        </Tooltip>

        {/* eslint-disable react/jsx-pascal-case */}
        <MRT_ToggleFiltersButton table={table} />
        <MRT_ShowHideColumnsButton table={table} />
        <MRT_ToggleFullScreenButton table={table} />
        {/* eslint-enable react/jsx-pascal-case */}
      </Box>
    ),
    renderEmptyRowsFallback: () => (
      <Typography sx={{ padding: theme.spacing(2) }}>No sitemap pages found.</Typography>
    ),
  });

  return (
    <Widget
      widgetSize={widgetSize}
      minHeight={minHeight}
      sx={{
        height: "auto",
        overflow: "visible",
      }}
    >
      <WidgetTitle title={"Site Map"} text={`${pageCount} Pages`} />

      {publicWebsite && (
        <Typography
          variant="body2"
          sx={{
            px: 2,
            pb: errorMessage ? 0 : 1,
            wordBreak: "break-word",
          }}
        >
          Public Website: {publicWebsite}
        </Typography>
      )}

      {!publicWebsite && (
        <Typography
          variant="body2"
          color="error"
          sx={{
            px: 2,
            pb: 1,
          }}
        >
          No public website has been set for this organization.
        </Typography>
      )}

      {errorMessage && (
        <Typography
          variant="body2"
          color="error"
          sx={{
            px: 2,
            pb: 1,
          }}
        >
          {errorMessage}
        </Typography>
      )}

      <WidgetSurveyContent>
        <Box
          sx={{
            height: "auto",
            overflow: "visible",
            pb: 2,
          }}
        >
          <MaterialReactTable table={table} />
        </Box>
      </WidgetSurveyContent>
    </Widget>
  );
};

export default BotpressSiteMap;
